import type { Dispatch, FC, SetStateAction } from 'react'
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { createContext } from 'use-context-selector'

import type { OccurrencesModal_salesAssociate$key } from '../../components/OcurrencesModal/__generated__/OccurrencesModal_salesAssociate.graphql'
import { ModalContentTemplate } from '../../shared/types'
import type { Occurrence, MultipleAssistancesData } from '../../shared/types'

type ModalOptions = {
  modalTitle: string
  onConfirm: (occurrences: Occurrence[]) => void
  salesAssociate: OccurrencesModal_salesAssociate$key
  serviceStartDate?: Date
  occurrences: Occurrence[]
  cutInLine?: boolean
}

type ContextType = {
  isOpen: boolean
  open: (options: ModalOptions) => void
  close: () => void
  options: ModalOptions
  selectedOccurrences: Occurrence[]
  setSelectedOccurrences: Dispatch<SetStateAction<Occurrence[]>>
  breadcrumbOccurrences: Occurrence[]
  setBreadcrumbOccurrences: Dispatch<SetStateAction<Occurrence[]>>
  displayedOccurences: Occurrence[]
  setDisplayedOccurrences: Dispatch<SetStateAction<Occurrence[]>>
  modalContentTemplate: ModalContentTemplate
  setModalContentTemplate: Dispatch<SetStateAction<ModalContentTemplate>>
  pendingConfirmOccurrence: Occurrence | null
  setPendingConfirmOccurrence: Dispatch<SetStateAction<Occurrence | null>>
  multipleAssistancesData: MultipleAssistancesData
  setMultipleAssistancesData: Dispatch<SetStateAction<MultipleAssistancesData>>
}

const emptyOptions = {
  modalTitle: '',
  onConfirm: () => {},
  salesAssociate: {} as OccurrencesModal_salesAssociate$key,
  occurrences: [],
}

export const OccurrencesModalContext = createContext({} as ContextType)

export const OccurrencesModalProvider: FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ModalOptions>(emptyOptions)
  const [selectedOccurrences, setSelectedOccurrences] = useState<Occurrence[]>(
    []
  )

  const [modalContentTemplate, setModalContentTemplate] = useState(
    ModalContentTemplate.Occurrences
  )

  const [
    pendingConfirmOccurrence,
    setPendingConfirmOccurrence,
  ] = useState<Occurrence | null>(null)

  const [breadcrumbOccurrences, setBreadcrumbOccurrences] = useState<
    Occurrence[]
  >([])

  const [displayedOccurences, _setDisplayedOccurrences] = useState(
    options.occurrences
  )

  const [
    multipleAssistancesData,
    setMultipleAssistancesData,
  ] = useState<MultipleAssistancesData>({
    active: false,
    assistancesCount: 1,
    currentAssistance: 1,
    occurrences: [],
  })

  const setDisplayedOccurrences = (
    occurrences: Occurrence[] | SetStateAction<Occurrence[]>
  ) => {
    _setDisplayedOccurrences(occurrences)
    setPendingConfirmOccurrence(null)
  }

  const open = useCallback((modalOptions: ModalOptions) => {
    setIsOpen(true)
    setOptions(modalOptions)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setOptions(emptyOptions)
    setPendingConfirmOccurrence(null)
    setSelectedOccurrences([])
    setBreadcrumbOccurrences([])
    setModalContentTemplate(ModalContentTemplate.Occurrences)

    setMultipleAssistancesData((prev) => ({
      ...prev,
      active: false,
      currentAssistance: 1,
    }))
  }, [])

  const contextValue = useMemo(
    () => ({
      isOpen,
      open,
      close,
      options,
      selectedOccurrences,
      setSelectedOccurrences,
      breadcrumbOccurrences,
      setBreadcrumbOccurrences,
      displayedOccurences,
      setDisplayedOccurrences,
      modalContentTemplate,
      setModalContentTemplate,
      pendingConfirmOccurrence,
      setPendingConfirmOccurrence,
      multipleAssistancesData,
      setMultipleAssistancesData,
    }),
    [
      isOpen,
      open,
      close,
      options,
      selectedOccurrences,
      breadcrumbOccurrences,
      displayedOccurences,
      modalContentTemplate,
      pendingConfirmOccurrence,
      multipleAssistancesData,
    ]
  )

  useEffect(() => {
    if (options.occurrences.length > 0) {
      _setDisplayedOccurrences(options.occurrences)
    }
  }, [options.occurrences])

  return (
    <OccurrencesModalContext.Provider value={contextValue}>
      {children}
    </OccurrencesModalContext.Provider>
  )
}
